import { Button, Shadow, HStack, VStack } from '@genial/components';
import { BoxRadius, Spacing, Typography } from '@genial/design-tokens';
import {
  FinanceEnumsFileSharingTypeEnum,
  FinanceEnumsFilterTypeEnum,
} from '@genial/services/dist/models';
import { useBreakpointValue } from '@genial/utils';
import { RouteProp, useNavigation, useRoute } from '@react-navigation/native';
import { Box, Text } from 'native-base';
import React from 'react';
import { useRecoilValue } from 'recoil';

import {
  CustomerDetailStackParamList,
  SignedInNavigationScreenNavigationProp,
} from '../../../../../../../routes/types';
import { useStatementsActions } from '../../../../../../../store/customerDetail/stataments/actions';
import {
  isExportingPdfForStatements,
  statementOptionQueryState,
  statementsFilterParamsState,
} from '../../../../../../../store/customerDetail/stataments/atom';
import { statementsRadioOptionsDisabled } from '../../../../../../../store/customerDetail/stataments/constants';
import {
  StatementOptionQuery,
  StatementsFilterParamsProps,
} from '../../../../../../../store/customerDetail/stataments/types';
import { ExportPdfDropdown } from '../../../../shared/ExportPdfDropdown';
import { SelectPeriod } from '../../SelectPeriod/Index';
import { SelectType } from '../../SelectType';

export type Option = { label: string; value: string };
export type LimitedOptionRange = { past?: Date; future?: Date };
export type SelectableOptionsProps = { title: { default: string; modal: string }; items: Option[] };

interface HeaderProps {
  selectOptions: SelectableOptionsProps;
  radioOptions: SelectableOptionsProps;
  hasData: boolean;
}

export const Header: React.FC<HeaderProps> = ({ radioOptions, selectOptions, hasData }) => {
  const route = useRoute<RouteProp<CustomerDetailStackParamList, 'CustomerDetail'>>();
  const { navigate } = useNavigation<SignedInNavigationScreenNavigationProp>();
  const isMobile = useBreakpointValue({ base: true, lg: false });

  const statementsFilterParams = useRecoilValue(statementsFilterParamsState);
  const statementOptionSelected = useRecoilValue(statementOptionQueryState);
  const isExportingPdf = useRecoilValue(isExportingPdfForStatements);

  const filterIsDisabled = statementsRadioOptionsDisabled.includes(statementOptionSelected);
  const { endDate, startDate } = statementsFilterParams;
  const { updateStatementOption, updateStatementsFilterParams, handleDownloadStatementPdf } =
    useStatementsActions();

  const selectedRadio = statementsFilterParams.filterType;

  const options = [radioOptions];

  const isExportButtonDisabled =
    !hasData ||
    isExportingPdf ||
    statementOptionSelected === 'direct-treasury' ||
    statementOptionSelected === 'fixed-income';

  const onChangeFilterType = (value: FinanceEnumsFilterTypeEnum) => {
    let data = {} as Partial<StatementsFilterParamsProps>;

    data = { filterType: value, endDate: undefined, startDate: undefined };

    if (value === 'Other') {
      const today = new Date();
      const yesterday = new Date(new Date().setDate(new Date().getDate() - 1));

      data.endDate = today;
      data.startDate = yesterday;
    }

    updateStatementsFilterParams(data);
  };

  const onExportPdf = (sharingType?: FinanceEnumsFileSharingTypeEnum) => {
    handleDownloadStatementPdf({
      accountNumber: route.params.accountNumber,
      filterType: statementsFilterParams.filterType,
      statementOption: statementOptionSelected,
      startDate: statementsFilterParams.startDate,
      endDate: statementsFilterParams.endDate,
      sharingType,
    });
  };

  const onChangeStatemenOptionQuery = (value: string) =>
    updateStatementOption(value as StatementOptionQuery);

  const onSelectDates = (initialDate: Date, finalDate: Date) => {
    updateStatementsFilterParams({
      startDate: initialDate,
      endDate: finalDate,
      ...(selectedRadio === 'Other' && { filterType: 'Other' }),
    });
  };

  const statementOptionSelectedLabelIndex = selectOptions.items.findIndex(
    (item) => item.value === (statementOptionSelected as unknown as string)
  );

  const getLimitAvaliableRange = (): LimitedOptionRange => {
    const today = new Date();
    const oneYearAgoLessOne = new Date(today);
    oneYearAgoLessOne.setDate(today.getDate() - 1);
    oneYearAgoLessOne.setFullYear(today.getFullYear() - 1);

    return {
      past: oneYearAgoLessOne,
    };
  };

  if (isMobile) {
    const filterTypeTransactionCount = statementOptionSelected !== 'current-account' ? 1 : 0;
    const filterPeriodDateCount = statementsFilterParams.filterType !== 'LastSevenDays' ? 1 : 0;
    const countFiltersApplied = filterTypeTransactionCount + filterPeriodDateCount;

    return (
      <VStack>
        <SelectType
          isDisabled={isExportingPdf}
          title={selectOptions.title.default}
          onValueChange={onChangeStatemenOptionQuery}
          options={selectOptions.items}
          selectedValue={selectOptions.items[statementOptionSelectedLabelIndex].label}
        />
        <HStack alignItems="center" justifyContent="space-between" mt={Spacing['stack.xxs']}>
          <Shadow level={5} borderRadius={BoxRadius.circular}>
            <Button
              variant="secondary"
              onPress={() => navigate('StatementsFilter', { options })}
              isDisabled={filterIsDisabled || isExportingPdf}>
              <HStack alignItems="center" justifyContent="center" space={Spacing['inset.quarck']}>
                <Text variant="semibold" fontSize={Typography['fontsize.md']}>
                  Filtrar
                </Text>
                {!filterIsDisabled && countFiltersApplied > 0 && (
                  <Box
                    rounded="full"
                    w="21px"
                    h="21px"
                    alignItems="center"
                    justifyContent="center"
                    bg={'brand.tertiary.light'}>
                    <Text
                      variant="semibold"
                      fontSize={Typography['fontsize.md']}
                      color={'neutral.white'}>
                      {countFiltersApplied}
                    </Text>
                  </Box>
                )}
              </HStack>
            </Button>
          </Shadow>
          <HStack alignItems="center">
            <ExportPdfDropdown
              isExporting={isExportingPdf}
              isDisabled={isExportButtonDisabled}
              onDownload={() => onExportPdf()}
              onSendToClient={() => onExportPdf('Email')}
            />
          </HStack>
        </HStack>
      </VStack>
    );
  }

  return (
    <HStack alignItems="center" justifyContent="space-between">
      <VStack space={Spacing['inline.xxxs']}>
        <SelectType
          isDisabled={isExportingPdf}
          title={selectOptions.title.default}
          onValueChange={onChangeStatemenOptionQuery}
          options={selectOptions.items}
          selectedValue={selectOptions.items[statementOptionSelectedLabelIndex].label}
        />
      </VStack>
      <HStack alignItems="center" space={Spacing['inline.nano']}>
        <SelectPeriod
          startDate={startDate!}
          endDate={endDate!}
          onSelectDates={onSelectDates}
          isDisabled={filterIsDisabled || isExportingPdf}
          dateRangePickerIsDisabled={filterIsDisabled || isExportingPdf}
          onClickPeriodInputDate={() => selectedRadio !== 'Other' && onChangeFilterType('Other')}
          title={radioOptions.title.default}
          onChange={(value) => onChangeFilterType(value as FinanceEnumsFilterTypeEnum)}
          options={radioOptions.items}
          selectedValue={!filterIsDisabled ? selectedRadio : ''}
          limitDateRange={getLimitAvaliableRange()}
        />
        <HStack alignItems="center">
          <ExportPdfDropdown
            isExporting={isExportingPdf}
            isDisabled={isExportButtonDisabled}
            onDownload={() => onExportPdf()}
            onSendToClient={() => onExportPdf('Email')}
          />
        </HStack>
      </HStack>
    </HStack>
  );
};
